import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Variable } from '@/features/pricing-models/types/model.types';
import { VariableLink } from '../types/quote.types';
import { VariableType } from '@/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Globe, Link2, Unlink, ArrowRight, RefreshCw, Loader2, HelpCircle } from 'lucide-react';
import { formatNumber } from '@/lib/utils/format';
import { masterDataService } from '@/features/master-data/services/master-data-service';

export interface AvailableSource {
  sectionId: string;
  sectionLabel: string;
  items: {
    itemId: string;
    itemLabel: string;
    totalPrice: number;
  }[];
}

interface GlobalVariablesCardProps {
  variables: Variable[];
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  variableLinks?: Record<string, VariableLink>;
  onLinkChange?: (links: Record<string, VariableLink>) => void;
  availableSources?: AvailableSource[];
}

export function GlobalVariablesCard({
  variables,
  values,
  onChange,
  variableLinks = {},
  onLinkChange,
  availableSources = [],
}: GlobalVariablesCardProps) {
  const [refreshingRate, setRefreshingRate] = useState(false);

  if (variables.length === 0) return null;

  const handleRefreshRate = async () => {
    setRefreshingRate(true);
    try {
      const rate =
        (await masterDataService.getCurrentExchangeRate('USDT')) ??
        (await masterDataService.getCurrentExchangeRate('USDC'));
      if (rate) {
        onChange({ ...values, TIPO_CAMBIO: rate });
      }
    } catch {
      // silencioso
    } finally {
      setRefreshingRate(false);
    }
  };

  const handleChange = (code: string, rawValue: string) => {
    const variable = variables.find((candidate) => candidate.code === code);
    const value =
      variable?.variableType === VariableType.TEXT ? rawValue : parseFloat(rawValue) || 0;
    onChange({ ...values, [code]: value });
  };

  const handleLink = (varCode: string, sourceSectionId: string, sourceItemId: string) => {
    if (!onLinkChange) return;
    const newLinks = { ...variableLinks, [varCode]: { sourceSectionId, sourceItemId } };
    onLinkChange(newLinks);
  };

  const handleUnlink = (varCode: string) => {
    if (!onLinkChange) return;
    const newLinks = { ...variableLinks };
    delete newLinks[varCode];
    onLinkChange(newLinks);
  };

  const hasSourcesAvailable =
    availableSources.length > 0 && availableSources.some((s) => s.items.length > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Variables Globales
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              Las variables globales aplican a todos los items de esta sección. Por ejemplo, el tipo
              de cambio o un porcentaje de margen se comparten entre todos los productos.
            </TooltipContent>
          </Tooltip>
        </CardTitle>
        <CardDescription>
          Valores compartidos entre todos los items de la cotización. Puedes vincular una variable
          al resultado de otra sección usando el icono de enlace.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {variables.map((variable) => {
            const link = variableLinks[variable.code!];
            const isLinked = !!link;

            // Find source info for display
            let sourceLabel = '';
            let sourceValue = 0;
            if (isLinked) {
              const srcSection = availableSources.find((s) => s.sectionId === link.sourceSectionId);
              const srcItem = srcSection?.items.find((i) => i.itemId === link.sourceItemId);
              sourceLabel =
                srcSection && srcItem
                  ? `${srcSection.sectionLabel} → ${srcItem.itemLabel}`
                  : 'Fuente no encontrada';
              sourceValue = srcItem?.totalPrice ?? 0;
            }

            return (
              <div key={variable.id} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`global-${variable.code}`} className="text-sm">
                    {variable.name} ({variable.code})
                  </Label>
                  {hasSourcesAvailable &&
                    onLinkChange &&
                    (isLinked ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-primary hover:text-info"
                            onClick={() => handleUnlink(variable.code!)}
                          >
                            <Unlink className="w-3.5 h-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Desvincular: volver a ingresar el valor manualmente
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <LinkPopover
                        varCode={variable.code!}
                        availableSources={availableSources}
                        onLink={handleLink}
                      />
                    ))}
                </div>

                {isLinked ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 px-2 py-1.5 bg-info-suave border border-info-borde rounded-md">
                      <Link2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      <span className="text-xs text-info truncate" title={sourceLabel}>
                        {sourceLabel}
                      </span>
                    </div>
                    <div className="text-sm font-mono font-semibold text-foreground px-2">
                      = {formatNumber(sourceValue)}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Input
                      id={`global-${variable.code}`}
                      type={
                        variable.variableType === VariableType.NUMBER ||
                        variable.variableType === VariableType.CURRENCY ||
                        variable.variableType === VariableType.PERCENTAGE
                          ? 'number'
                          : 'text'
                      }
                      step="0.01"
                      value={values[variable.code!] ?? ''}
                      onChange={(e) => handleChange(variable.code!, e.target.value)}
                      placeholder={variable.defaultValue?.toString() || '0'}
                    />
                    {variable.code === 'TIPO_CAMBIO' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            onClick={handleRefreshRate}
                            disabled={refreshingRate}
                          >
                            {refreshingRate ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Obtener el tipo de cambio actual USDT/BOB desde los exchanges P2P
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )}

                {variable.description && (
                  <p className="text-xs text-muted-foreground">{variable.description}</p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Popover for selecting a source section + item to link
 */
function LinkPopover({
  varCode,
  availableSources,
  onLink,
}: {
  varCode: string;
  availableSources: AvailableSource[];
  onLink: (varCode: string, sectionId: string, itemId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');

  const selectedSection = availableSources.find((s) => s.sectionId === selectedSectionId);

  const handleConfirm = () => {
    if (selectedSectionId && selectedItemId) {
      onLink(varCode, selectedSectionId, selectedItemId);
      setOpen(false);
      setSelectedSectionId('');
      setSelectedItemId('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-primary"
            >
              <Link2 className="w-3.5 h-3.5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          Vincular a un item de otra sección para tomar su valor automáticamente
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-80 p-4" align="end">
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold">Vincular variable</p>
            <p className="text-xs text-muted-foreground">
              Selecciona un item de otra sección como fuente de valor
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Sección origen</Label>
            <SearchableSelect
              options={availableSources.map((src) => ({
                value: src.sectionId,
                label: src.sectionLabel,
              }))}
              value={selectedSectionId || undefined}
              onValueChange={(val) => {
                setSelectedSectionId(val);
                setSelectedItemId('');
              }}
              placeholder="Selecciona sección"
              searchPlaceholder="Buscar sección..."
              className="h-8 text-xs"
            />
          </div>

          {selectedSection && (
            <div className="space-y-2">
              <Label className="text-xs">Item origen</Label>
              <SearchableSelect
                options={selectedSection.items.map((item) => ({
                  value: item.itemId,
                  label: `${item.itemLabel} (${formatNumber(item.totalPrice)})`,
                }))}
                value={selectedItemId || undefined}
                onValueChange={setSelectedItemId}
                placeholder="Selecciona item"
                searchPlaceholder="Buscar item..."
                className="h-8 text-xs"
              />
            </div>
          )}

          {selectedItemId && (
            <div className="flex items-center gap-1 text-xs text-ok bg-ok-suave px-2 py-1 rounded">
              <ArrowRight className="w-3 h-3" />
              Valor:{' '}
              {formatNumber(
                selectedSection?.items.find((i) => i.itemId === selectedItemId)?.totalPrice ?? 0
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!selectedSectionId || !selectedItemId}
              onClick={handleConfirm}
            >
              <Link2 className="w-3.5 h-3.5 mr-1" />
              Vincular
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default GlobalVariablesCard;
